import { ReleaseType } from '@ibk/shared-types';
import { isActiveStatus } from '../utils';
const ECHOED_BENEFICIARY_FIELDS = ['beneficiaryId', 'value', 'daysQuantity', 'discountValue'];
function isObject(value) {
    return typeof value === 'object' && value !== null;
}
function isDeepEqual(left, right) {
    if (Object.is(left, right)) {
        return true;
    }
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
            return false;
        }
        return left.every((value, index) => isDeepEqual(value, right[index]));
    }
    if (!isObject(left) || !isObject(right)) {
        return false;
    }
    const leftKeys = Object.keys(left)
        .filter(key => left[key] !== undefined)
        .sort((first, second) => first.localeCompare(second));
    const rightKeys = Object.keys(right)
        .filter(key => right[key] !== undefined)
        .sort((first, second) => first.localeCompare(second));
    return isDeepEqual(leftKeys, rightKeys) && leftKeys.every(key => isDeepEqual(left[key], right[key]));
}
function isAllowedEcho(current, requestValue, responseValue) {
    return isDeepEqual(current, requestValue) || isDeepEqual(current, responseValue);
}
function indexBeneficiaries(beneficiaries) {
    const indexed = new Map();
    for (const beneficiary of beneficiaries) {
        if (!beneficiary.beneficiaryId || indexed.has(beneficiary.beneficiaryId)) {
            return null;
        }
        indexed.set(beneficiary.beneficiaryId, beneficiary);
    }
    return indexed;
}
function haveSameBeneficiaryIds(left, right) {
    const leftById = indexBeneficiaries(left);
    const rightById = indexBeneficiaries(right);
    return (leftById !== null &&
        rightById !== null &&
        leftById.size === rightById.size &&
        Array.from(leftById.keys()).every(beneficiaryId => rightById.has(beneficiaryId)));
}
function omitEchoedBeneficiaryFields(beneficiary) {
    const entries = Object.entries(beneficiary).filter(([key]) => !ECHOED_BENEFICIARY_FIELDS.includes(key));
    return Object.fromEntries(entries);
}
function areBeneficiaryListsEquivalent(current, request, response) {
    if (isDeepEqual(current, request) || (request === undefined && isDeepEqual(current, response))) {
        return true;
    }
    if (current?.length !== request?.length || current?.length !== response.length) {
        return false;
    }
    if (current === undefined || request === undefined) {
        return false;
    }
    const currentById = indexBeneficiaries(current);
    const requestById = indexBeneficiaries(request);
    const responseById = indexBeneficiaries(response);
    if (!currentById || !requestById || !responseById) {
        return false;
    }
    return Array.from(currentById.entries()).every(([beneficiaryId, currentBeneficiary]) => {
        const requestBeneficiary = requestById.get(beneficiaryId);
        const responseBeneficiary = responseById.get(beneficiaryId);
        if (!requestBeneficiary || !responseBeneficiary) {
            return false;
        }
        const unknownFieldsMatchRequest = isDeepEqual(omitEchoedBeneficiaryFields(currentBeneficiary), omitEchoedBeneficiaryFields(requestBeneficiary));
        return (unknownFieldsMatchRequest &&
            isAllowedEcho(currentBeneficiary.value, requestBeneficiary.value, responseBeneficiary.value) &&
            isAllowedEcho(currentBeneficiary.daysQuantity, requestBeneficiary.daysQuantity, responseBeneficiary.daysQuantity) &&
            isAllowedEcho(currentBeneficiary.discountValue, requestBeneficiary.discountValue, responseBeneficiary.discountValue));
    });
}
function projectResponseBeneficiaries(response, releaseType, includeIndividualDiscount) {
    if (!Array.isArray(response.beneficiaries)) {
        return null;
    }
    const projected = response.beneficiaries
        .filter(beneficiary => isActiveStatus(beneficiary.statusInProduct))
        .map(beneficiary => {
        const result = {
            beneficiaryId: beneficiary.id,
        };
        if (releaseType === ReleaseType.DAILY) {
            result.daysQuantity = beneficiary.productDaysQuantity ?? 0;
        }
        else {
            result.value = beneficiary.productValue ?? 0;
        }
        if (includeIndividualDiscount) {
            result.discountValue = beneficiary.productDiscountValue ?? 0;
        }
        return result;
    });
    return indexBeneficiaries(projected) ? projected : null;
}
function areVouchersEquivalent(current, request, response) {
    const { creditDate: currentCreditDate, releaseType: currentReleaseType, beneficiaries: currentBeneficiaries, ...currentExactFields } = current;
    const { creditDate: requestCreditDate, releaseType: requestReleaseType, beneficiaries: requestBeneficiaries, ...requestExactFields } = request;
    if (!isDeepEqual(currentExactFields, requestExactFields) ||
        !isAllowedEcho(currentCreditDate, requestCreditDate, response.creditDate) ||
        !isAllowedEcho(currentReleaseType, requestReleaseType, response.releaseType)) {
        return false;
    }
    const responseBeneficiaries = projectResponseBeneficiaries(response, currentReleaseType, current.isIndividualDiscount === true);
    if (responseBeneficiaries === null ||
        (requestBeneficiaries !== undefined && !haveSameBeneficiaryIds(requestBeneficiaries, responseBeneficiaries))) {
        return false;
    }
    return areBeneficiaryListsEquivalent(currentBeneficiaries, requestBeneficiaries, responseBeneficiaries);
}
export function canReuseRechargeSimulation(voucherContractId, currentRequest, record) {
    if (!record ||
        !voucherContractId ||
        voucherContractId !== record.voucherContractId ||
        !Array.isArray(currentRequest.vouchers) ||
        currentRequest.vouchers.length !== 1 ||
        !Array.isArray(record.request.vouchers) ||
        record.request.vouchers.length !== 1 ||
        !Array.isArray(record.response.vouchers) ||
        record.response.vouchers.length !== 1) {
        return false;
    }
    const requestVoucher = record.request.vouchers[0];
    const responseVoucher = record.response.vouchers[0];
    if (!requestVoucher.voucherTypeId ||
        requestVoucher.voucherTypeId !== responseVoucher.voucherTypeId ||
        !responseVoucher.creditDate ||
        !responseVoucher.releaseType ||
        !Array.isArray(responseVoucher.beneficiaries)) {
        return false;
    }
    const { vouchers: currentVouchers, ...currentExactFields } = currentRequest;
    const { vouchers: requestVouchers, ...requestExactFields } = record.request;
    return (isDeepEqual(currentExactFields, requestExactFields) &&
        areVouchersEquivalent(currentVouchers[0], requestVouchers[0], responseVoucher));
}
